"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { declineWipe, markWiped } from "@/lib/breach/assignments"
import type { Workspace } from "@/lib/breach/workspace"
import { AssignForm } from "./assign-form"
import type { WorkspaceUpdater } from "./incident-tools"

const toLocalInput = (d: Date) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16) }
const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "")

/** "Did you wipe the device?" Wiped (and when), hand it to someone, or decided not to. All three go on the Timeline. */
export function WipeStatus({ itemId, w, update }: { itemId: string; w: Workspace; update: WorkspaceUpdater }) {
  const c = w.checks[itemId]
  const [mode, setMode] = useState<"none" | "wiped" | "declined" | "assign">("none")
  const [at, setAt] = useState(toLocalInput(new Date()))
  const [reason, setReason] = useState("")
  const assigned = w.assignments.filter((a) => a.itemId === itemId && a.status === "open")

  if (c?.done) {
    return (
      <p className="rounded-md border border-border bg-background/60 p-2 text-sm" role="status">
        {c.declined ? <>Decided not to wipe it{c.note ? `: ${c.note}` : ""}.</> : <>Wiped {c.doneAt ? `at ${fmt(c.doneAt)}` : ""}.</>}{" "}
        <button type="button" className="underline print:hidden" onClick={() => update((x) => ({ ...x, checks: { ...x.checks, [itemId]: { ...(x.checks[itemId] ?? { done: false }), done: false, doneAt: undefined, declined: undefined, note: undefined } } }))}>Change</button>
      </p>
    )
  }
  return (
    <div className="grid gap-2 rounded-md border border-border bg-background/60 p-3 print:hidden" role="group" aria-label="Did you wipe the device?">
      <p className="text-sm font-medium">Have you wiped the device?</p>
      {assigned.length > 0 && <p className="text-xs text-muted-foreground">Assigned to {assigned.map((a) => a.assigneeName).join(", ")}. Mark it done on the Assignments tab when they tell you it&apos;s done.</p>}
      {mode === "none" && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setMode("wiped")}>Yes, I wiped it</Button>
          <Button size="sm" variant="outline" onClick={() => setMode("assign")}>Not yet: assign someone</Button>
          <Button size="sm" variant="ghost" onClick={() => setMode("declined")}>I&apos;m not wiping it</Button>
        </div>
      )}
      {mode === "wiped" && (
        <div className="grid gap-2">
          <label className="grid gap-1 text-xs">When did you wipe it?
            <input type="datetime-local" className="rounded-md border border-border bg-background p-2 text-sm" value={at} onChange={(e) => setAt(e.target.value)} />
          </label>
          <div className="flex gap-2"><Button size="sm" onClick={() => { update((x) => markWiped(x, itemId, at ? new Date(at) : new Date())); setMode("none") }}>Save</Button><Button size="sm" variant="ghost" onClick={() => setMode("none")}>Cancel</Button></div>
        </div>
      )}
      {mode === "declined" && (
        <div className="grid gap-2">
          <label className="grid gap-1 text-xs">Why not? (it goes on your Timeline)
            <input className="rounded-md border border-border bg-background p-2 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example: it has been recovered" />
          </label>
          <div className="flex gap-2"><Button size="sm" onClick={() => { update((x) => declineWipe(x, itemId, reason)); setMode("none") }}>Save</Button><Button size="sm" variant="ghost" onClick={() => setMode("none")}>Cancel</Button></div>
        </div>
      )}
      {mode === "assign" && <AssignForm w={w} update={update} defaults={{ title: "Remotely wipe the lost device", itemId }} onClose={() => setMode("none")} />}
    </div>
  )
}
